import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import {
  baseFarmRiskTypes,
  FarmRiskType,
  FarmScheduleCoverageType,
  fo6StructureRoofMaterial,
  fo6StructureRoofShape,
  paymentMethod,
  StructureCategory
} from '@ourbranch/lookups';
import { getIn } from 'formik';
import {
  ABOVE_GROUND_POOL_INCLUDED_LIMIT,
  hasInlandMarineSppItems,
  hasRateableScheduledFarmPropertyItems,
  hasMixedInlandMarineRiskTypes,
  isDwellingRiskType,
  isInlandMarineAvailableForPolicyType,
  isInlandMarineRiskType,
  isPoolLimitApproved,
  isRentersRiskType,
  isWindHailDeductibleOverridden
} from '@ourbranch/farm-utils';

import { getFarmInterestedPartyItemSchema } from 'common/components/farm/additional-party/add-farm-interested-party.validation';
import { isCosmeticDamageUwOverridden } from 'common/helpers/cosmetic-damage-exclusion-helpers';

import {
  validHomePurchaseDate,
  validUSState,
  requiredForRisks,
  requiredIfFO6BarnsOutbuildings,
  requiredIfFO6SilosGrainBins,
  requiredIfFO6SilosGrainBinsNonTower,
  requiredIfFO6ConfinementBuildings,
  requiredIfFO6HoopBuildings,
  requiredIfFO6DwellingContents,
  requiredIfFO6Fencing,
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled,
  requiredIfFO6DwellingContentsAndHeatingEnabled,
  requiredForTowers,
  requiredForGrainStorage,
  requiredForNonTowerSilos,
  requiredIfFO6SilosGrainBinsAndHeatingEnabled,
  requiredIfFO6PoultryAndHeatingEnabled,
  validYearBuilt,
  requiredWhenCanEditCoverageAmount,
  coverageInRangeRequiredWhenEditable,
  mobileHomeSqFtValidation,
  isDwellingOrRentersRisk,
  nullifyIfNaN,
  requiredForFO6Structures,
  rc80PercentRequirement,
  invalidMobileHomeConfigForFo3,
  requiresSwimmingPoolType
} from 'common/helpers/yup-helpers';
import { brandConstants, appConfig } from 'brand-config';
import {
  getAdditionalFarmRiskTypes,
  getConstructionTypeExemptPropertyTypes,
  isLiabilityCoverageHiddenByMask,
  isMiscBoat
} from 'common/helpers/farm-helpers';

// #33279: the inland-marine per-item deductible is the "All peril deductible" stored on
// extendedDetails.deductibleAllOther (the legacy top-level `deductible` field was removed from the
// UI). These categories require a deductible in every state; every other SPP category requires one
// only outside IL — Forreston (IL) does not force a per-item deductible.
const SPP_ALWAYS_REQUIRED_DEDUCTIBLE_CATEGORIES = ['scheduledGlass', 'computerHardware', 'computerSoftware'];

// #34019: Gator/Utility Transport Vehicle is a full clone of ATV, so every SPP/asset required-field
// rule that keys off 'atv' must treat it identically. Keeps this offer-level schema in sync with
// inland-marine.jsx / not-on-policy-asset.jsx (their drift is what #33277/#33279 fixed for boats).
const GATOR_UTV = 'gatorUtilityTransportVehicle';
const isAtvLikeCategory = (category) => category === 'atv' || category === GATOR_UTV;
const sppDeductibleRequired = (category, context) => {
  if (SPP_ALWAYS_REQUIRED_DEDUCTIBLE_CATEGORIES.includes(category)) {
    return true;
  }
  const state = context?.farmLocations?.[0]?.address?.state || context?.correctedAddress?.state;
  return state !== 'IL';
};

/**
 * Helper function to get building class 1 maximum age from brand configuration
 * @param {string} state - State code (e.g., 'IL', 'MO')
 * @returns {number} Maximum age in years for Building Class 1 (defaults to 25)
 */
const getBuildingClass1MaxAge = (state = 'IL') => {
  return appConfig?.farm?.[state]?.risks[FarmRiskType.Fo6Structures]?.businessRules?.buildingClass1MaxAgeYears ?? 25;
};

/**
 * Helper function to detect if a risk is new (not yet saved)
 * @param {object} formRiskData - The risk data from form context
 * @returns {boolean} True if the risk is new (has no id), false otherwise
 */
const isNewRisk = (formRiskData) => !formRiskData?.id;

// FO-6 structure categories with a roof shape and material to enter (#37180). Hoop buildings and Quonset barns
// get theirs from their construction; silos, fencing and solar panels have none.
const FO6_ROOFED_CATEGORIES = [
  StructureCategory.BarnsOutbuildings,
  StructureCategory.ConfinementBuildings,
  StructureCategory.PoultryBuildings,
  StructureCategory.DwellingContents
];

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'requiredForRisks', requiredForRisks);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6BarnsOutbuildingsAndHeatingEnabled',
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled
);
Yup.addMethod(Yup.mixed, 'requiredForTowers', requiredForTowers);
Yup.addMethod(Yup.mixed, 'requiredForGrainStorage', requiredForGrainStorage);
Yup.addMethod(Yup.mixed, 'requiredForNonTowerSilos', requiredForNonTowerSilos);
Yup.addMethod(Yup.mixed, 'requiredIfFO6SilosGrainBinsAndHeatingEnabled', requiredIfFO6SilosGrainBinsAndHeatingEnabled);
Yup.addMethod(Yup.mixed, 'requiredIfFO6PoultryAndHeatingEnabled', requiredIfFO6PoultryAndHeatingEnabled);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6DwellingContentsAndHeatingEnabled',
  requiredIfFO6DwellingContentsAndHeatingEnabled
);
Yup.addMethod(Yup.string, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBinsNonTower', requiredIfFO6SilosGrainBinsNonTower);
Yup.addMethod(Yup.string, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.number, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6Fencing', requiredIfFO6Fencing);
Yup.addMethod(Yup.number, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.mixed, 'requiredWhenCanEditCoverageAmount', requiredWhenCanEditCoverageAmount);
Yup.addMethod(Yup.mixed, 'coverageInRangeRequiredWhenEditable', coverageInRangeRequiredWhenEditable);
Yup.addMethod(Yup.string, 'validYearBuilt', validYearBuilt);
Yup.addMethod(Yup.number, 'mobileHomeSqFtValidation', mobileHomeSqFtValidation);
Yup.addMethod(Yup.number, 'nullifyIfNaN', nullifyIfNaN);
Yup.addMethod(Yup.string, 'requiredForFO6Structures', requiredForFO6Structures);
Yup.addMethod(Yup.number, 'rc80PercentRequirement', rc80PercentRequirement);
Yup.addMethod(Yup.string, 'invalidMobileHomeConfigForFo3', invalidMobileHomeConfigForFo3);

/**
 * Barn Building Class Coverage Validation
 *
 * Validates coverage fields for FO6 Barns/Outbuildings based on building class:
 * - Class 1 & 2: Must use RC loss settlement, Special Form coverage, and Snow Weight coverage
 * - Class 3: Must use ACV loss settlement (Special Form and Snow Weight must be off)
 *
 * Matches BE enforcement in farm-post-data-rules.ts:698-730
 * Related issues: #29098, #29153
 */
export const barnClassCoverageValidation = function (message) {
  return this.test('barn-class-coverage-validation', message, function (value) {
    // Get risk type and building data from correct context
    // this.from[1].value should be the risk object when validating coverage fields
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Barns/Outbuildings
    if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
      return true;
    }

    const buildingClass = getIn(formRiskData, 'building.buildingClass');
    const field = this.path.split('.').pop();

    // Class 1 & 2: Must use RC + Special Form + Snow Weight
    if (buildingClass === 1 || buildingClass === 2) {
      if (field === 'structureLossSettlement' && value !== 'RC') {
        return this.createError({
          message: `Building Class ${buildingClass} barns must use Replacement Cost loss settlement`
        });
      }

      if (field === 'specialFormCoverage' && value !== true) {
        return this.createError({
          message: `Building Class ${buildingClass} barns must include Special Form coverage`
        });
      }

      if (field === 'hasSnowWeightCoverage' && value !== true) {
        return this.createError({
          message: `Building Class ${buildingClass} barns must include Weight of Ice, Snow, or Sleet coverage`
        });
      }

      return true;
    }

    // Class 3: Must use ACV, no Special Form, no Snow Weight
    if (buildingClass === 3) {
      if (field === 'structureLossSettlement' && value !== 'ACV') {
        return this.createError({
          message: 'Building Class 3 barns must use Actual Cash Value loss settlement'
        });
      }

      if (field === 'specialFormCoverage' && value === true) {
        return this.createError({
          message: 'Building Class 3 barns cannot have Special Form coverage'
        });
      }

      if (field === 'hasSnowWeightCoverage' && value === true) {
        return this.createError({
          message: 'Building Class 3 barns cannot have Weight of Ice, Snow, or Sleet coverage'
        });
      }

      return true;
    }

    return true;
  });
};

// Register the method (backwards-compatible alias)
Yup.addMethod(Yup.mixed, 'barnType1Validation', barnClassCoverageValidation);

/**
 * Coverage C Validation
 *
 * Validates Coverage C based on residence usage and brandConfig rules.
 * - FO1: Residence-usage-based rules (Issue #28614)
 * - FO2: Loss-settlement + residence-usage rules from brandConfig (Issue #29011)
 * - FO3: Residence-usage rules from brandConfig (Issue #29011)
 * - FO4: Minimum $10,000, no maximum (Issue #29011)
 */
export const validateDwellingCoverageC = function (message) {
  return this.test('fo1-coverage-c-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const coverageA = getIn(formRiskData, 'coverages.coverageA');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    const minimumElectedCoverageC = appConfig?.farm?.[state]?.coverageLimits?.minimumElectedCoverageC;
    const passOrElectedMinimumError = () =>
      minimumElectedCoverageC && value > 0 && value < minimumElectedCoverageC
        ? this.createError({
            message: `Coverage C must be $0 or at least $${minimumElectedCoverageC.toLocaleString()}`
          })
        : true;

    // With C capped at an A under the minimum, every C above $0 is unrateable, so $0 wins over the per-form minimum
    if (
      isDwellingRiskType(riskType) &&
      residenceUsage !== 'R' &&
      minimumElectedCoverageC &&
      coverageA > 0 &&
      coverageA < minimumElectedCoverageC &&
      value !== null &&
      value < minimumElectedCoverageC
    ) {
      return value === 0
        ? true
        : this.createError({
            message: `Coverage C must be $0 when Coverage A is under $${minimumElectedCoverageC.toLocaleString()}`
          });
    }

    // FO1 validation
    if (riskType === FarmRiskType.Fo1) {
      const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageC;
      if (!businessRules?.byResidenceUsage) {
        return passOrElectedMinimumError(); // No business rules defined
      }

      const rules = businessRules.byResidenceUsage[residenceUsage];
      if (!rules) {
        return passOrElectedMinimumError(); // No rules for this residence usage
      }

      const currentValue = Number(value) || 0;
      const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

      if (currentValue < rules.min) {
        return this.createError({
          message: `Coverage C cannot be less than $${rules.min.toLocaleString()}`
        });
      }

      if (currentValue > maxValue) {
        if (residenceUsage === 'R') {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()} for tenant occupied rental properties`
          });
        }
        return this.createError({
          message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
        });
      }

      return passOrElectedMinimumError();
    }

    // FO2 and FO3: brandConfig-driven Coverage C validation (Issue #29011)
    if ([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(riskType)) {
      const lossSettlement = getIn(formRiskData, 'coverages.lossSettlement');
      const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageC;

      // Resolve rules: FO2 uses byLossSettlement (ACV vs RC differ), FO3 uses byResidenceUsage
      let rules;
      if (businessRules?.byLossSettlement && lossSettlement) {
        rules = businessRules.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
      }
      // Fall through to byResidenceUsage as secondary lookup regardless of byLossSettlement presence
      if (!rules && businessRules?.byResidenceUsage && residenceUsage) {
        rules = businessRules.byResidenceUsage[residenceUsage];
      }

      if (rules) {
        const minValue = rules.minPctOfCoverageA ? coverageA * rules.minPctOfCoverageA : (rules.min ?? 0);
        const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

        if (value < minValue) {
          const minLabel = rules.minPctOfCoverageA
            ? `$${minValue.toLocaleString()} (${rules.minPctOfCoverageA * 100}% of Coverage A)`
            : `$${minValue.toLocaleString()}`;
          return this.createError({
            message: `Coverage C must be at least ${minLabel}`
          });
        }

        if (value > maxValue) {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
          });
        }

        return passOrElectedMinimumError();
      }

      return passOrElectedMinimumError();
    }

    // FO4: Minimum $10,000, no maximum (Issue #29011)
    if (riskType === FarmRiskType.Fo4) {
      if (value != null && value < 10000) {
        return this.createError({
          message: 'Coverage C must be at least $10,000'
        });
      }

      return true;
    }

    return passOrElectedMinimumError();
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateDwellingCoverageC', validateDwellingCoverageC);

/**
 * Dwelling Coverage D Validation (FO1 / FO2 / FO3)
 *
 * Enforces the brand-config Coverage D rule for the risk's residence usage (and, for FO2, loss
 * settlement). Behavior by rule shape:
 * - `percentOfCoverageA` set (locked cell, e.g. Forreston, or CFM FO2-RC / FO3 Primary/Seasonal):
 *   skip — the value is auto-derived as a percent of Coverage A server-side, not user-entered.
 * - editable cell with `min`/`max` (e.g. CFM FO1 / FO2-ACV $0–$3,000): enforce the [min, max] range.
 * - editable cell with no `max` (#33149 — CFM FO2-RC / FO3 tenant "increase to any amount"): enforce
 *   only the min floor; there is no upper cap.
 *
 * Previously this validated FO1 only; FO2/FO3 Coverage D had no schema-level bounds. Generalized for
 * #33149 so the ticket's floors ("cannot decrease below $3,000") and caps ("cannot exceed $3,000")
 * are enforced in the UI for FO2/FO3 too.
 *
 * Related issues: #28614, #29855, #32082, #33149
 */
export const validateDwellingCoverageD = function (message) {
  return this.test('dwelling-coverage-d-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(riskType)) {
      return true;
    }

    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const lossSettlement = getIn(formRiskData, 'coverages.lossSettlement');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageD;
    if (!businessRules) {
      return true; // No business rules defined
    }

    // Resolve rule: FO2 uses byLossSettlement (ACV vs RC differ); FO1/FO3 use byResidenceUsage.
    let rules;
    if (businessRules.byLossSettlement && lossSettlement) {
      rules = businessRules.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
    }
    if (!rules && businessRules.byResidenceUsage && residenceUsage) {
      rules = businessRules.byResidenceUsage[residenceUsage];
    }
    if (!rules) {
      return true; // No rule for this residence usage / loss settlement
    }

    // Skip min/max validation when coverage D is auto-calculated as a percentage of Coverage A
    if (rules.percentOfCoverageA) {
      return true;
    }

    const currentValue = Number(value) || 0;

    if (currentValue < rules.min) {
      return this.createError({
        message: `Coverage D cannot be less than $${rules.min.toLocaleString()}`
      });
    }

    // A rule with no `max` (#33149 — "increase to any amount") has no upper cap.
    if (rules.max != null && currentValue > rules.max) {
      // #34528: Vacant-Rental ('VR') is vacant too — same "no Coverage D" message as Vacant ('V').
      if (residenceUsage === 'V' || residenceUsage === 'VR') {
        return this.createError({
          message: 'Coverage D is not available for vacant properties'
        });
      }
      return this.createError({
        message: `Coverage D cannot exceed $${rules.max.toLocaleString()}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateDwellingCoverageD', validateDwellingCoverageD);

/**
 * Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible.
 * Wind/Hail deductible is always stored as a decimal percentage (0.01 = 1%, 0.015 = 1.5%, 0.02 = 2%)
 * and must be converted to a dollar amount using coverageA before comparison.
 *
 * Related issue: #28860
 */
export const validateWindHailDeductible = function (message) {
  return this.test('wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO1, FO2, FO3, FO4 (dwelling risks)
    if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Get Coverage A to calculate dollar amounts for percentage deductibles
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    // Get the reference coverage to calculate dollar amounts for percentage deductibles
    // Percentage deductibles are represented as values < 1 (e.g., 0.01 = 1%)
    // FO-4 (renters) has no Coverage A — use Coverage C instead
    const referenceCoverage = isRentersRiskType(riskType)
      ? getIn(formRiskData, 'coverages.coverageC')
      : getIn(formRiskData, 'coverages.coverageA');

    // Calculate actual dollar amounts
    // If value < 1, it's a percentage deductible, multiply by the reference coverage
    const windHailDollarAmount =
      windHailValue < 1 ? (referenceCoverage ? windHailValue * referenceCoverage : null) : windHailValue;

    const allPerilDollarAmount =
      allPerilValue < 1 ? (referenceCoverage ? allPerilValue * referenceCoverage : null) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing
    if (windHailValue < 1 && windHailDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based Wind/Hail deductible'
      });
    }

    // If all-peril is a percentage but coverage is missing
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based All-Peril deductible'
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = Math.round(windHailDollarAmount);
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${+(windHailValue * 100).toFixed(2)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${+(allPerilValue * 100).toFixed(2)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay})`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateWindHailDeductible', validateWindHailDeductible);

/**
 * FO6 Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible for FO6 risks.
 * Handles different coverage types:
 * - FO6Structures: Uses coverageE
 * - FO6Scheduled: Uses coverageF
 * - FO6Unscheduled: Uses coverageG (read-only, but validate for completeness)
 *
 * Wind/Hail deductible can be either a decimal percentage (< 1, e.g., 0.01 = 1%) or a dollar amount (>= 1)
 * and must be converted to a dollar amount using the appropriate coverage before comparison.
 *
 * Related issue: #28860
 */
export const validateFO6WindHailDeductible = function (message) {
  return this.test('fo6-wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO6 risks
    if (![FarmRiskType.Fo6Structures, FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(riskType)) {
      return true;
    }

    // #36996: an approved exception (GO's 1% on an imported CFM structure) stands, while the value is
    // still the one approved — the same value entered without an approval is still rejected below.
    if (
      isWindHailDeductibleOverridden({
        deductibleWindHail: value,
        windHailDeductibleOverride: getIn(formRiskData, 'coverages.windHailDeductibleOverride')
      })
    ) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Determine which coverage to use based on FO6 risk type
    let coverage = 0;
    let coverageLabel = '';

    if (riskType === FarmRiskType.Fo6Structures) {
      coverage = getIn(formRiskData, 'coverages.coverageE') || 0;
      coverageLabel = 'E';
    } else if (riskType === FarmRiskType.Fo6Scheduled) {
      // coverageF is an array of items with 'limit' property - sum all limits
      const coverageFArray = getIn(formRiskData, 'coverages.coverageF');
      coverage = Array.isArray(coverageFArray)
        ? coverageFArray.reduce((sum, item) => sum + (item?.limit || 0), 0)
        : coverageFArray || 0;
      coverageLabel = 'F';
    } else if (riskType === FarmRiskType.Fo6Unscheduled) {
      coverage = getIn(formRiskData, 'coverages.coverageG') || 0;
      coverageLabel = 'G';
    }

    // Calculate actual dollar amounts
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    const windHailDollarAmount = windHailValue < 1 ? (coverage ? windHailValue * coverage : null) : windHailValue;

    // All-Peril can be either a percentage (< 1) or dollar amount (>= 1)
    const allPerilDollarAmount =
      allPerilValue < 1 ? (coverage ? Math.round(coverage * allPerilValue) : 0) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing, reject
    if (windHailValue < 1 && windHailDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based Wind/Hail deductible`
      });
    }

    // If all-peril is a percentage but coverage is missing, reject
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based All-Peril deductible`
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = windHailDollarAmount !== null ? Math.round(windHailDollarAmount) : null;
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded !== null && windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${+(windHailValue * 100).toFixed(2)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${+(allPerilValue * 100).toFixed(2)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay}) for Coverage ${coverageLabel}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateFO6WindHailDeductible', validateFO6WindHailDeductible);

/**
 * Minimum All-Peril Deductible by Coverage A (issue #32441, CFM)
 *
 * For the dwelling forms (FO1/FO2/FO3), the All-Peril deductible must meet a minimum tied to the
 * Coverage A limit. The bands are brand-config-driven so the rule only applies where a brand opts in
 * (CFM), and never blocks brands that don't (e.g. Forreston, which carries $1,500 dwelling
 * deductibles). Config shape: appConfig.farm[state].risks[riskType].businessRules.deductibleAllOther
 *   .minByCoverageA = [{ minCoverageA, minDeductible }, ...]  (any order; highest matching band wins).
 *
 * CFM bands (spec sheet): >=250k -> $2,500, >=500k -> $5,000, >=750k -> $10,000.
 * FO4 (renters/contents, no Coverage A) is exempt.
 */
export const validateMinDeductibleForCoverageA = function (message) {
  return this.test('min-deductible-for-coverage-a', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only dwelling forms have a Coverage A minimum-deductible rule
    if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(riskType)) {
      return true;
    }

    // No deductible set yet — nothing to validate (required-ness handled elsewhere)
    if (value == null) {
      return true;
    }

    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;
    const bands = appConfig?.farm?.[state]?.risks?.[riskType]?.businessRules?.deductibleAllOther?.minByCoverageA;

    // Brand/state without configured bands (e.g. Forreston) — rule does not apply
    if (!Array.isArray(bands) || bands.length === 0) {
      return true;
    }

    const coverageA = Number(getIn(formRiskData, 'coverages.coverageA')) || 0;

    // Highest band whose threshold the Coverage A limit meets
    const matchingBand = bands
      .filter((band) => coverageA >= band.minCoverageA)
      .sort((a, b) => b.minCoverageA - a.minCoverageA)[0];

    if (!matchingBand) {
      return true;
    }

    if (Number(value) < matchingBand.minDeductible) {
      return this.createError({
        message: `Minimum ${matchingBand.minDeductible.toLocaleString()} deductible, please fix`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateMinDeductibleForCoverageA', validateMinDeductibleForCoverageA);

/**
 * Minimum All-Peril Deductible for FO6 Scheduled Coverage F items (issue #33662, CFM)
 *
 * Each scheduled farm-property item carries its own All-Peril deductible. Two brand-config-driven
 * minimums apply, read from appConfig.farm[state].risks.FO6Scheduled.coverageF:
 *   - minDeductibleByValue = [{ minValue, minDeductible }, ...] — an item whose value (limit) exceeds
 *     minValue must meet minDeductible (CFM: value over $500,000 -> $2,500). "Over", so strictly >.
 *   - specialCattleEndorsementMinDeductible — electing the Special Cattle Endorsement raises the
 *     minimum (CFM: $1,000), so the $250 livestock default alone can't be kept with the endorsement.
 * The higher applicable minimum wins. The rule is skipped for brands/states with no config (so it
 * never blocks Forreston) and for new (unsaved) risks, mirroring validateMinDeductibleForCoverageA.
 *
 * Attached to each coverageF item's `deductible`, so this.parent is the item and this.from walks
 * item -> coverages -> risk.
 */
export const validateCoverageFItemDeductible = function (message) {
  return this.test('min-deductible-for-coverage-f-item', message, function (value) {
    // this.from: [0] = item, [1] = coverages, [2] = risk
    const risk = this.from?.[2]?.value;
    if (!risk || risk.type !== FarmRiskType.Fo6Scheduled) {
      return true;
    }

    // Skip new/unsaved risks, matching the Coverage A minimum-deductible rule.
    if (isNewRisk(risk)) {
      return true;
    }

    // No deductible set yet — nothing to validate (required-ness handled elsewhere).
    if (value == null) {
      return true;
    }

    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;
    const coverageFConfig = appConfig?.farm?.[state]?.risks?.[FarmRiskType.Fo6Scheduled]?.coverageF;
    if (!coverageFConfig) {
      return true; // Brand/state without config (e.g. Forreston) — rule does not apply.
    }

    const item = this.parent;
    let requiredMin = 0;

    const valueBands = coverageFConfig.minDeductibleByValue;
    if (Array.isArray(valueBands) && valueBands.length > 0) {
      const itemValue = Number(item?.limit) || 0;
      const matchingBand = valueBands
        .filter((band) => itemValue > band.minValue)
        .sort((a, b) => b.minValue - a.minValue)[0];
      if (matchingBand) {
        requiredMin = Math.max(requiredMin, matchingBand.minDeductible);
      }
    }

    // Mirror the CFM rater's field resolution (base-risk-answers.handler): the Special Cattle
    // Endorsement is elected when `hasSpecialCattleEndorsement` is set, falling back to legacy
    // `multipleLossCoverage` for migrated data — so validation and rating agree on what's elected.
    const endorsementElected = item?.hasSpecialCattleEndorsement ?? item?.multipleLossCoverage;
    if (endorsementElected && coverageFConfig.specialCattleEndorsementMinDeductible) {
      requiredMin = Math.max(requiredMin, coverageFConfig.specialCattleEndorsementMinDeductible);
    }

    if (requiredMin > 0 && Number(value) < requiredMin) {
      return this.createError({
        message: `Minimum ${requiredMin.toLocaleString()} deductible, please fix`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateCoverageFItemDeductible', validateCoverageFItemDeductible);

/**
 * FO6 Structure Category Loss Settlement Validation
 *
 * This validation method enforces the business rules for structure category loss settlement
 * options at the schema level. It works in conjunction with the UI business logic in
 * FO6.utils.js to ensure data integrity.
 *
 * Validation Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Must be ACV only (roof: ACV or NONE with exclusion)
 * - Confinement, Poultry: Structure → ACV or RC; Roof → ACV, RC_ACV_WIND_HAIL, or NONE with exclusion
 * - Silos/Grain Bins: ACV or RC allowed (roof also allows NONE with exclusion; RC_ACV_WIND_HAIL is NOT valid)
 * - Barns/Outbuildings: Handled by existing building class validation
 *
 * @param {string} fieldName - Name of the field being validated (for error messages)
 * @param {string} message - Base validation error message
 * @returns {Yup.TestFunction} Yup test function
 */
export const validateStructureCategoryLossSettlement = function (fieldName, message) {
  return this.test(`${fieldName}-structure-category-validation`, message, function (value) {
    // #33509: migrated FO6 structures came over with structureLossSettlement left null for RC-settled
    // buildings (#33297), which this rule rejects ("must be ACV or RC"). A UW servicing a migrated-and-
    // renewed term shouldn't be blocked by that legacy null gap on an unrelated edit — but scope the
    // relaxation to the *null* value only: a non-null bad value has no backend analog and must still be
    // caught. Mirrors the other migration-relaxable fields (roof shape).
    if (
      this.options.context?.relaxFarmMigrationRequirements &&
      (value === null || value === undefined || value === '')
    ) {
      return true;
    }
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Structures
    if (riskType !== FarmRiskType.Fo6Structures) {
      return true;
    }

    // Structure category-specific validation
    switch (buildingType) {
      case StructureCategory.DwellingContents:
      case StructureCategory.HoopBuildings:
      case StructureCategory.Fencing: {
        // Must be ACV only (NONE allowed for roof loss settlement when roof exclusion is enabled)
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'NONE'] : ['ACV'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" for this structure type`
          })
        );
      }

      case StructureCategory.ConfinementBuildings:
      case StructureCategory.PoultryBuildings: {
        // For structureLossSettlement: ACV or RC allowed (role restrictions handled in UI)
        // For roofLossSettlement: ACV, RC_ACV_WIND_HAIL, or straight RC.
        // #33652/#33651: #33613 added straight "Replacement Cost" (RC) as a UW-only roof upgrade for
        // Confinement/Poultry (in getRoofLossSettlementOptions), matching Type 1/2 barns (#33578) — but
        // this validator still rejected it, so a UW selecting RC was blocked with "must be a valid
        // option for this structure type". RC is now accepted (role-gated by the UI dropdown + backend
        // post-data-rules downgrade, exactly like RC_ACV_WIND_HAIL). NONE allowed when roof exclusion
        // is enabled. GitHub issue #29079.
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC_ACV_WIND_HAIL', 'RC', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: isRoofField
              ? `${fieldName} must be a valid option for this structure type`
              : `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.SilosGrainBins: {
        // ACV or RC allowed for both structure and roof loss settlement (RC_ACV_WIND_HAIL is NOT valid for silos)
        // NONE allowed for roof loss settlement when roof exclusion is enabled
        // GitHub issue #29079
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.BarnsOutbuildings:
        // Building class logic already handles this
        return true;

      default:
        return true;
    }
  });
};

// Register the method
Yup.addMethod(Yup.string, 'validateStructureCategoryLossSettlement', validateStructureCategoryLossSettlement);

const baseFarmValidationSchema = Yup.object().shape({
  farmCoverage: Yup.object().shape({
    policyLevelAdjustmentFactor: Yup.number()
      .transform((value, originalValue) => {
        // Convert empty strings to default value
        if (String(originalValue).trim() === '') {
          return 0;
        }
        // Convert NaN to default value
        if (Number.isNaN(value)) {
          return 0;
        }
        return value ?? 0;
      })
      .required()
      .min(-99, 'Adjustment factor must be between -99% and 99%')
      .max(99, 'Adjustment factor must be between -99% and 99%'),
    adjustmentRationale: Yup.string().nullable().optional().max(1000, 'Rationale must be 1,000 characters or fewer'),
    extraExpenseLimit: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // Convert empty strings to null
        if (String(originalValue).trim() === '') {
          return null;
        }
        return value;
      })
      .test('min-when-provided', 'Farm Extra Expense Coverage minimum limit is $5,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value >= 5000;
      })
      .test('max-when-provided', 'Farm Extra Expense Coverage maximum limit is $25,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value <= 25000;
      })
  }),
  entity: Yup.object()
    .shape({
      id: Yup.string().nullable(),
      type: Yup.string()
        .nullable()
        .required('Add details about the entity requesting this offer')
        .test('joint-requires-coapplicant', 'A co-applicant is required when entity type is Joint', function (value) {
          // Only validate on Modify Offer (when offer.id exists)
          const offerId = this.options?.context?.offerId;

          // Skip validation for New Offer (no offer ID yet)
          if (!offerId) {
            return true;
          }

          // Only validate if entity type is "Joint"
          if (value === 'Joint') {
            // Get drivers array from root form values
            const formValues = this.from?.[this.from.length - 1]?.value;
            const drivers = formValues?.drivers;

            // Require at least 2 drivers AND one with isCoApplicant === true
            if (!drivers || drivers.length < 2) {
              return false;
            }

            const hasCoApplicant = drivers.some((driver) => driver.isCoApplicant === true);
            return hasCoApplicant;
          }

          return true;
        }),
      name: Yup.string()
        .nullable()
        .test('required-for-non-individual-entity', 'Entity name is required', function (value) {
          const entityType = this.parent.type;
          if (entityType && entityType !== 'Individual' && entityType !== 'Joint') {
            return !!value;
          }
          return true;
        }),
      address: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().nullable(),
          zip: Yup.string().nullable()
        })
        .nullable()
    })
    .test('has-entity-type', 'Add details about the entity requesting this offer', (value) => {
      // Ensure entity with a type is provided
      return value?.type;
    }),
  farmLocations: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      description: Yup.string().required('Description is required'),
      legalDescription: Yup.object().shape({
        format: Yup.string().required('Format is required'),
        section: Yup.string().nullable(),
        township: Yup.string().nullable(),
        townshipCoordinates: Yup.string().nullable(),
        range: Yup.string().nullable(),
        rangeCoordinates: Yup.string().nullable(),
        lot: Yup.string().nullable(),
        block: Yup.string().nullable()
      }),
      additionalLegalDescription: Yup.string().nullable(),
      locationDirections: Yup.string().nullable(),
      numberOfAcres: Yup.number().nullable(),
      address: Yup.object().shape({
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        country: Yup.string().nullable(),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zip: Yup.string().required('Zip code is required')
      }),
      fpc: Yup.string().nullable(),
      fpcFromLocation: Yup.string().nullable(),
      fireHydrantWithin1000ft: Yup.boolean().nullable(),
      latitude: Yup.number().nullifyIfNaN().nullable(),
      longitude: Yup.number().nullifyIfNaN().nullable(),
      risks: Yup.array().of(
        Yup.object().shape({
          type: Yup.string().required('Risk type is required'),
          description: Yup.string().required('Description is required'),
          adjustmentFactor: Yup.number()
            .transform((value, originalValue) => {
              // Convert empty strings to default value
              if (String(originalValue).trim() === '') {
                return 0;
              }
              // Convert NaN to default value
              if (Number.isNaN(value)) {
                return 0;
              }
              return value ?? 0;
            })
            .required()
            .min(-99, 'Adjustment factor must be between -99% and 99%')
            .max(99, 'Adjustment factor must be between -99% and 99%'),
          adjustmentFactorTotal: Yup.number()
            .nullable()
            .min(-99, 'Adjustment factor total must be between -99% and 99%')
            .max(99, 'Adjustment factor total must be between -99% and 99%'),
          building: Yup.object().shape({
            basementSqFt: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Basement sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            basementType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Basement/Foundation is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            constructionType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Construction type is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBuildings
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.DwellingContents
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo3,
                    requireIfRatedOnly: true,
                    excludedPropertyTypes: ({ state }) =>
                      getConstructionTypeExemptPropertyTypes(state, FarmRiskType.Fo3)
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Construction type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            mobileHomeQualityGrade: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              }),
            mobileHomeConfig: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Mobile home config is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              })
              .invalidMobileHomeConfigForFo3(
                'Single-wide and double-wide mobile homes are not eligible for FO-3. Please select FO-1 or FO-2.'
              ),
            exteriorWallType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Exterior wall finish is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageCarCapacity: Yup.number()
              .nullable()
              .requiredForRisks({
                message: "Garage's car space is required",
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Garage type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            dwellingLocation: Yup.object().shape({
              address: Yup.string().nullable(),
              address2: Yup.string().nullable(),
              country: Yup.string().nullable(),
              city: Yup.string().nullable(),
              state: Yup.string().nullable(),
              zip: Yup.string().nullable()
            }),
            homeQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFamiliesInDwelling: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Families in dwelling is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFireplaces: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Fireplaces is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFullBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Full bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numHalfBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Half bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numStories: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Stories is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numWoodBurningStoves: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Wood stoves is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            ownershipStatus: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Ownership status is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            purchaseDate: Yup.string().nullable(),
            purchaseAmount: Yup.number().nullable(),
            roofOriginal: Yup.boolean().nullable(),
            roofShape: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof shape is required!',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }],
                // #33509: a UW servicing a migrated-and-renewed term can proceed without a roof shape
                // the converter couldn't fill, so it doesn't block an unrelated edit.
                relaxOnFarmMigration: true
              })
              // #37180: an FO-6 structure's roof shape (the 360Value rooftype). Required on a new structure only:
              // a migrated one has none (IMT and GO carry no roof shape) and one saved before still holds it in
              // roofType until renewal moves it.
              .requiredForRisks({
                message: 'Roof shape is required',
                // A structure saved before (rated or not) holds its shape in roofType; the shape select shows it.
                readValue: (_roofShape, building) => fo6StructureRoofShape(building),
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: FO6_ROOFED_CATEGORIES,
                    requireIfNewOnly: true
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof shape is required', function (value) {
                if (this.options.context?.relaxFarmMigrationRequirements) {
                  return true;
                }
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofDeck: Yup.string().nullable(),
            roofCover: Yup.string().nullable(),
            roofDeckAttachment: Yup.string().nullable(),
            roofToWallConnection: Yup.string().nullable(),
            openingProtection: Yup.string().nullable(),
            roofSWR: Yup.string().nullable(),
            roofType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof type is required',
                risks: [
                  // FO1/FO2 farm dwellings feed Clarion Door's roof_type_age factor lookup; a null
                  // roof type fails rating outright with BLOCK 5042 (#33731). Required only once the
                  // risk has been rated (`requireIfRatedOnly`), matching FO3: a newly-added dwelling
                  // gets its roof type/year from the 360Value/Verisk pull that runs server-side
                  // during the rate, so requiring it BEFORE the first rate blocked that pull and left
                  // added dwellings stuck on a roof fix-it (#33807). The rater defaults a still-null
                  // roof safely so the first rate can't BLOCK 5042 (dwelling-risk-answers.handler).
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                ]
              })
              // #37180: on an FO-6 structure roofType is the roof MATERIAL (its shape is roofShape). Required on a
              // new structure only: an existing one renewed out of the old single "Roof type" select (which stored
              // the shape here) has no material, and nothing rates or values on it.
              .requiredForRisks({
                message: 'Roof material is required',
                readValue: (_roofType, building) => fo6StructureRoofMaterial(building),
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: FO6_ROOFED_CATEGORIES,
                    requireIfNewOnly: true
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofYear: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Roof year is required',
                risks: [
                  // FO1/FO2 farm dwellings: roof year drives the roof_age rating input; required
                  // only once rated (`requireIfRatedOnly`), matching roof type above and FO3. A
                  // newly-added dwelling gets roof year from the server-side 360/Verisk pull that
                  // runs during the rate, so requiring it before the first rate blocked that pull
                  // (#33807). See the roofType note above.
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof year is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .requiredIfFO6DwellingContents('Roof year is required')
              .test('roof-year-max-4-digits', 'Roof year must be at most 4 digits', (value) => {
                if (value == null) {
                  return true;
                }
                return String(value).length <= 4;
              })
              .test(
                'roof-year-after-year-built',
                'Roof year must be same as or later than year built',
                function (value) {
                  const yearBuilt = getIn(this.parent, 'yearBuilt');

                  // Skip validation if either value is missing
                  if (!value || !yearBuilt) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(yearBuilt, 10);
                  const roofYearNum = parseInt(value, 10);

                  if (roofYearNum < yearBuiltNum) {
                    return this.createError({
                      message: `Roof year (${roofYearNum}) cannot be earlier than building year (${yearBuiltNum})`
                    });
                  }

                  return true;
                }
              ),
            roofYearChangedManually: Yup.boolean().nullable(),
            sqFt: Yup.number()
              .nullable()
              .requiredForNonTowerSilos('Square footage is required')
              .requiredForRisks({
                message: 'Square footage is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true }
                ]
              })
              .test('required-for-fo3-barndominium', 'Square footage is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .min(150, 'Square footage must be greater than 150 square feet')
              .mobileHomeSqFtValidation(),
            originalSqFt: Yup.number().nullable(),
            swimmingPool: Yup.object()
              .nullable()
              .default(undefined)
              .shape({
                typeOfPool: Yup.string()
                  .nullable()
                  .test('required-swimming-pool-type', 'Type of pool is required', function (value) {
                    // Swimming pool type is intentionally required for FO1, FO2, FO3, FO4 risk types.
                    // This ensures users must explicitly select a pool type (or "None") before finalizing.
                    // Check if this risk type requires swimming pool type
                    if (requiresSwimmingPoolType(this.options)) {
                      return !!value;
                    }
                    return true;
                  }),
                hasRemovableStepsOrLockingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-above-ground',
                    'This field is required for above-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'A') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                has4FtHighFenceWithSelfLatchingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-in-ground',
                    'This field is required for in-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'I') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                hasDivingBoardOrPoolSlide: Yup.boolean()
                  .nullable()
                  .test('required-for-dwelling-with-pool', 'This field is required', function (value) {
                    const typeOfPool = getIn(this.parent, 'typeOfPool');
                    if (isDwellingOrRentersRisk(this.options) && (typeOfPool === 'A' || typeOfPool === 'I')) {
                      return value !== null && value !== undefined;
                    }
                    return true;
                  }),
                limit: Yup.number().nullable()
              }),
            typeOfHome: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            occupancy: Yup.string()
              .nullable()
              .requiredIfFO6BarnsOutbuildings('Occupancy is required')
              .requiredIfFO6SilosGrainBins('Occupancy is required')
              .requiredIfFO6ConfinementBuildings('Occupancy is required'),
            residenceType: Yup.string().nullable().requiredIfFO6DwellingContents('Residence type is required'),
            yearBuilt: Yup.string()
              .nullable()
              .validYearBuilt()
              .test('year-built-max-4-digits', 'Year built must be at most 4 digits', (value) => {
                if (value == null) {
                  return true;
                }
                return String(value).length <= 4;
              })
              .requiredForRisks({
                message: 'Year built is required',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Year built is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null && String(value).trim() !== '';
                }
                return true;
              })
              .requiredForFO6Structures('Year built is required')
              .requiredIfFO6DwellingContents('Year built is required')
              .test(
                'year-built-building-class-1-compatibility',
                'Building age incompatible with Building Class 1 requirements',
                function (value) {
                  // Access sibling fields within the building object
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Skip risk type check for manual validation - rely on field conditions only
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  if (
                    buildingClass !== 1 ||
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings
                  ) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const yearBuilt = parseInt(value, 10);
                  const buildingAge = yearBuilt ? currentYear - yearBuilt : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Building Class 1 requires age ≤ maxAge years AND no open sides
                  const shouldFail = hasOpenSides || (buildingAge !== null && buildingAge > maxAge);

                  if (shouldFail) {
                    return false;
                  }

                  return true;
                }
              )
              .test(
                'year-built-before-roof-year',
                'Building year must be same as or earlier than roof year',
                function (value) {
                  const roofYear = getIn(this.parent, 'roofYear');

                  // Skip validation if either value is missing
                  if (!value || !roofYear) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(value, 10);
                  const roofYearNum = parseInt(roofYear, 10);

                  if (yearBuiltNum > roofYearNum) {
                    return this.createError({
                      message: `Building year (${yearBuiltNum}) cannot be later than roof year (${roofYearNum})`
                    });
                  }

                  return true;
                }
              ),
            latitude: Yup.number().nullifyIfNaN().nullable(),
            longitude: Yup.number().nullifyIfNaN().nullable(),
            stormShutters: Yup.boolean().nullable(),
            porchSqFt: Yup.number().nullable(),
            porchCode: Yup.string().nullable(),
            constructionStyle: Yup.string().nullable(),
            areasUnderConstruction: Yup.boolean()
              .nullable()
              .test('renovation-fields-required', 'Renovation details are incomplete', function (value) {
                if (!value) {
                  return true;
                }
                const renovation = this.parent?.renovation;
                const renovationFields = [
                  'electricalType',
                  'electricalService',
                  'heatingType',
                  'heatingYear',
                  'coolingYear',
                  'plumbingYear',
                  'electricalYear'
                ];
                const hasMissing = renovationFields.some((field) => !renovation?.[field]);
                if (!hasMissing) {
                  return true;
                }
                const riskDescription = this.from?.[1]?.value?.description || 'this risk';
                return this.createError({
                  message: `You've indicated ${riskDescription} has renovations, please provide those details or uncheck the box to continue`
                });
              }),
            hasTrampoline: Yup.boolean().nullable(),
            // Mobile home specific fields
            tieDownType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Tie down type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                ]
              }),
            enclosedMasonryFoundation: Yup.boolean().nullable(),
            fullSkirtingAroundBase: Yup.boolean().nullable(),
            // Fields for both Barndominium and MobileHome
            renovation: Yup.object()
              .shape({
                electricalType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalService: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical service is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                coolingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'AC year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                plumbingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Plumbing year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  })
              })
              .nullable(),
            attachedStructureSqFt: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Attached garage sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            // Well pump - all fields required if any is filled
            numWellPumps: Yup.number()
              .nullable()
              .test('well-pump-required', 'Number of pumps is required', function (value) {
                const { wellPumpEstimatedValue, wellPumpDescription } = this.parent;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasEstValue || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              })
              // 0 is the un-applied section (the form's "Applied" tag and Remove treat it so, and the GO
              // import writes 0 when there's no well pump coverage), so only a count between 0 and 1
              // is invalid; a 0 that comes with a value or description fails 'well-pump-required'. #37040
              .test('well-pump-min', 'Number of pumps must be at least 1', (value) => {
                if (value != null && value !== 0) {
                  return value >= 1;
                }
                return true;
              }),
            wellPumpEstimatedValue: Yup.number()
              .nullable()
              .test('well-pump-value-required', 'Estimated value is required', function (value) {
                const { numWellPumps, wellPumpDescription } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasNumPumps || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              }),
            wellPumpDescription: Yup.string()
              .nullable()
              .test('well-pump-description-required', 'Description of pumps or motors is required', function (value) {
                const { numWellPumps, wellPumpEstimatedValue } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;

                if (hasNumPumps || hasEstValue) {
                  return value != null && value.trim() !== '';
                }
                return true;
              }),
            numSolidFuelBurningAppliances: Yup.number()
              .nullable()
              .min(0, 'Number of appliances cannot be negative')
              .requiredForRisks({
                message: 'Solid fuel burning appliances count is required',
                risks: [
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  // #34545: FO3 was the odd one out — solid-fuel count was only required for
                  // Barndominium ('B') / MobileHome ('MH'). FO1/FO2 require it on every rated risk,
                  // so FO3 now matches (required once rated, any property type).
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [StructureCategory.BarnsOutbuildings, StructureCategory.PoultryBuildings]
                  },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              }),
            primaryHeatingSource: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Primary heating source is required',
                risks: [
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Primary heating source is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            secondaryHeatingSource: Yup.string().nullable(),
            buildingQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Quality is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              }),
            buildingClass: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Building class is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [StructureCategory.BarnsOutbuildings]
                  }
                ]
              })
              .test(
                'building-class-1-age-eligibility',
                'Building Class 1 requires eligible building age and no open sides',
                function (value) {
                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  const buildingType = getIn(this.parent, 'buildingType');
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  if (
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings ||
                    value !== 1
                  ) {
                    return true;
                  }

                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Class 1 requires age ≤ maxAge years AND no open sides
                  if (buildingAge !== null && buildingAge > maxAge) {
                    return this.createError({
                      message: `Building Class 1 requires building age ≤ ${maxAge} years. Current building age: ${buildingAge} years.`
                    });
                  }

                  if (hasOpenSides) {
                    return this.createError({
                      message: 'Building Class 1 cannot have open sides.'
                    });
                  }
                  return true;
                }
              ),
            structureOpenSides: Yup.boolean()
              .nullable()
              .test('no-open-sides-for-class-1', 'Class 1 structures cannot have open sides', function (value) {
                const buildingClass = getIn(this.parent, 'buildingClass');
                return !(buildingClass === 1 && value === true);
              })
              .test(
                'no-open-sides-for-barn-type-1',
                'Buildings of this age cannot have open sides (violates Type 1 requirements)',
                function (value) {
                  // Access sibling fields within the building object
                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type from parent context (navigate up to risk level)
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }

                  const riskType = getIn(formRiskData, 'type');

                  // Only validate for FO6 Barns/Outbuildings with Class 1
                  // Class 2 and 3 are allowed to have open sides regardless of age
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  if (buildingClass !== 1) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // If building age <= maxAge, it would be Type 1, so cannot have open sides
                  if (buildingAge !== null && buildingAge <= maxAge && value === true) {
                    return this.createError({
                      message: `Buildings ${maxAge} years old or newer cannot have open sides (violates Type 1 requirements)`
                    });
                  }

                  return true;
                }
              )
              .test(
                'open-sides-type-1-eligibility',
                'Type 1 barns cannot have open sides. Remove Type 1 coverages or fix building characteristics.',
                function (value) {
                  // Access sibling fields within the building object
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type and coverage data
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }
                  const riskType = getIn(formRiskData, 'type');
                  const coverages = getIn(formRiskData, 'coverages');

                  // Only validate for FO6 Barns/Outbuildings
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  // Class 2 and 3 are allowed to have open sides even with the same coverages
                  if (buildingClass !== 1) {
                    return true;
                  }

                  // Check if this appears to be configured as Type 1 (has Type 1 coverages)
                  const hasType1Coverages =
                    coverages?.structureLossSettlement === 'RC' &&
                    coverages?.specialFormCoverage === true &&
                    coverages?.hasSnowWeightCoverage === true;

                  // If configured with Type 1 coverages but has open sides, block
                  if (hasType1Coverages && value === true) {
                    return this.createError({
                      message: 'Building configured as Type 1 cannot have open sides.'
                    });
                  }

                  return true;
                }
              ),
            numHeatingSources: Yup.number().nullable(),
            numSecondaryHeatingSources: Yup.number().nullable(),
            diameter: Yup.string().nullable().requiredForTowers('Diameter is required'),
            height: Yup.string()
              .nullable()
              .test('required-sg-height', 'Height is required', function (value) {
                const buildingType = getIn(this.parent, 'buildingType');
                const occupancy = getIn(this.parent, 'occupancy');
                // Height is hidden for Bulk Bin/Hopper (GHH) and length/width types (CC, GS, GH)
                const hiddenHeightOccupancies = ['GHH', 'CC', 'GS', 'GH'];
                if (buildingType === 'SG' && occupancy && !hiddenHeightOccupancies.includes(occupancy)) {
                  return !!value;
                }
                return true;
              }),
            length: Yup.string().nullable(),
            width: Yup.string().nullable(),
            bushelCapacity: Yup.string().nullable().requiredForGrainStorage('Bushel capacity is required'),
            containsInGroundPool: Yup.boolean().nullable(),
            residenceUsage: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Residence usage is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }],
                requireIfRatedOnly: true
              }),
            propertyType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Structure category is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }]
              })
          }),
          coverages: Yup.object().shape({
            coverageA: Yup.number()
              .coverageInRangeRequiredWhenEditable([FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo3] }),
            coverageB: Yup.number().nullable(),
            // #35617: authorized Coverage A/E limit override. When on, the coverageA/coverageE floor/cap
            // rules (in yup-helpers coverageInRangeRequiredWhenEditable + rc80PercentRequirement) are
            // bypassed and a reason becomes required. Mirrors the roofExclusionDescription pattern.
            limitOverride: Yup.boolean().nullable(),
            limitOverrideBy: Yup.string().nullable(),
            limitOverrideAt: Yup.string().nullable(),
            limitOverrideRemovedBy: Yup.string().nullable(),
            limitOverrideRemovedAt: Yup.string().nullable(),
            limitOverrideReason: Yup.string()
              .nullable()
              .when('limitOverride', {
                is: (value) => !!value,
                // `.trim()` so a whitespace-only reason doesn't satisfy `.required()`.
                then: (schema) => schema.trim().required('Please enter a reason for the coverage limit override'),
                otherwise: (schema) => schema.nullable()
              }),
            coverageC: Yup.number().nullable().validateDwellingCoverageC('Invalid Coverage C value'),
            coverageD: Yup.number().nullable().validateDwellingCoverageD('Invalid Coverage D value'),
            coverageE: Yup.number()
              .coverageInRangeRequiredWhenEditable([
                FarmRiskType.Fo6Unscheduled,
                FarmRiskType.Fo6Scheduled,
                FarmRiskType.Fo6Structures
              ])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo6Structures] })
              .requiredIfFO6DwellingContents('Coverage E - Dwelling Limit is required')
              .requiredIfFO6Fencing('Coverage E - Dwelling Limit is required'),
            coverageF: Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string().nullable(),
                  category: Yup.string().nullable(),
                  limit: Yup.number().nullable(),
                  description: Yup.string().nullable(),
                  // #33662: per-item all-peril deductible minimum (value over $500k -> $2,500; Special
                  // Cattle Endorsement -> $1,000). Config-driven, so it never blocks non-CFM brands.
                  deductible: Yup.number().nullable().validateCoverageFItemDeductible('Deductible below minimum'),
                  deductibleWindHail: Yup.number().nullable(),
                  additionalPerils: Yup.number().nullable(),
                  multipleLossCoverage: Yup.boolean().nullable(),
                  livestockCategory: Yup.string().nullable(),
                  hasOpenPerils: Yup.boolean().nullable(),
                  hasReplacementCostCoverage: Yup.boolean().nullable(),
                  serialNumber: Yup.string().nullable(),
                  make: Yup.string().nullable(),
                  increaseStackLimit: Yup.number().nullable(),
                  quantity: Yup.number().nullable().integer().min(0),
                  hasWinterPerils: Yup.boolean().nullable(),
                  hasSpecialCattleEndorsement: Yup.boolean().nullable(),
                  hasSuffocationOfLivestock: Yup.boolean().nullable()
                })
              )
              .nullable(),
            coverageG: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Blanket limit is required',
                risks: [{ risk: FarmRiskType.Fo6Unscheduled }]
              })
              .test('coverageG-greater-than-zero', 'Blanket limit must be greater than $0', function (value) {
                const risk = this.from?.[1]?.value;
                if (!risk || risk.type !== FarmRiskType.Fo6Unscheduled) {
                  return true;
                }
                if (isNewRisk(risk)) {
                  return true;
                }
                if (value === null || value === undefined) {
                  return true; // handled by requiredForRisks
                }
                return value > 0;
              }),
            coverageMS: Yup.boolean().nullable(),
            hasActiveCoverageMS: Yup.boolean().nullable(),
            deductible: Yup.number().nullable(),
            deductibleAllOther: Yup.number()
              .nullable()
              .validateMinDeductibleForCoverageA('All-Peril deductible is below the minimum for this Coverage A limit'),
            deductibleWindHail: Yup.number()
              .nullable()
              .validateWindHailDeductible('Invalid Wind/Hail deductible')
              .validateFO6WindHailDeductible('Invalid Wind/Hail deductible for FO6'),
            deductibleEarthquake: Yup.number().nullable(),
            deductibleEarthquakeDwellingContents: Yup.number().nullable(),
            roofExclusion: Yup.string().nullable(),
            roofExclusionDetails: Yup.string().nullable(),
            // #33612 (item G): FO6 roof exclusion/restriction — the "Description of reason … (required)"
            // field must actually be required whenever the toggle is on (both "Roof Excluded" and
            // "Roof Actual Cash Value"). Mirrors exteriorWallSurfacingExclusionDetails below.
            hasRoofExclusionRestriction: Yup.boolean().nullable(),
            roofExclusionRestriction: Yup.string().nullable(),
            roofExclusionDescription: Yup.string()
              .nullable()
              .when('hasRoofExclusionRestriction', {
                is: (value) => !!value,
                // #33651: `.trim()` so a whitespace-only reason (e.g. a lone newline) doesn't satisfy
                // `.required()` — agents were reaching UW review without a real reason.
                then: (schema) => schema.trim().required('Please enter a reason for the roof exclusion/restriction'),
                otherwise: (schema) => schema.nullable()
              }),
            exteriorWallSurfacingExclusion: Yup.boolean().nullable(),
            exteriorWallSurfacingExclusionDetails: Yup.string()
              .nullable()
              .when('exteriorWallSurfacingExclusion', {
                is: (value) => !!value,
                // #33651: `.trim()` for the same whitespace-only bypass as roofExclusionDescription above.
                then: (schema) =>
                  schema.trim().required('Description is required when exterior wall surfacing exclusion is enabled'),
                otherwise: (schema) => schema.nullable()
              }),
            otherStructuresExclusion: Yup.boolean().nullable(),
            otherStructuresExclusionDetails: Yup.string().nullable(),
            hasSnowWeightCoverage: Yup.boolean().nullable().barnType1Validation(),
            structureLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Structure Loss Settlement')
              .barnType1Validation(),
            roofLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Roof Loss Settlement')
              .test('roof-acv-constraint', 'Roof Loss Settlement cannot be RC when Structure is ACV', function (value) {
                const structureLoss = getIn(this.parent, 'structureLossSettlement');
                if (structureLoss === 'ACV' && value === 'RC') {
                  return this.createError({
                    message:
                      'Roof Loss Settlement cannot be "Replacement Cost" when Structure Loss Settlement is "Actual Cash Value"'
                  });
                }
                return true;
              }),
            specialFormCoverage: Yup.boolean().nullable().barnType1Validation(),
            windHailExclusion: Yup.boolean().nullable(),
            windHailExclusionDetails: Yup.string().nullable(),
            otherStructuresRoofExclusion: Yup.boolean().nullable(),
            sidingExclusion: Yup.boolean().nullable(),
            sidingExclusionDetails: Yup.string().nullable(),
            stormDamageExclusion: Yup.boolean().nullable(),
            stormDamageExclusionDetails: Yup.string().nullable(),
            scheduleCoverages: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    coverageType: Yup.string().nullable(),
                    items: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.string().nullable(),
                          category: Yup.string()
                            .nullable()
                            .test('category-required-by-coverage-type', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && !value) {
                                return this.createError({ message: 'Kind of animal is required' });
                              }
                              if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                return this.createError({ message: 'Type of item is required' });
                              }
                              if (ct === FarmScheduleCoverageType.INCOME && !value) {
                                return this.createError({ message: 'Type of item is required' });
                              }
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Item type is required' });
                              }
                              return true;
                            }),
                          name: Yup.string()
                            .nullable()
                            .test('name-required-spp', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Item name is required' });
                              }
                              return true;
                            }),
                          limit: Yup.number()
                            .nullable()
                            .test('limit-required-by-coverage-type', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (
                                ct === FarmScheduleCoverageType.ANIMALS_4H_FFA ||
                                ct === FarmScheduleCoverageType.PEAK ||
                                ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES ||
                                ct === FarmScheduleCoverageType.SPP
                              ) {
                                if (value == null) {
                                  return this.createError({ message: 'Coverage limit is required' });
                                }
                              }
                              if (ct === FarmScheduleCoverageType.INCOME) {
                                if (value == null) {
                                  return this.createError({ message: 'Limit is required' });
                                }
                                if (value < 5000) {
                                  return this.createError({ message: 'Limit minimum is $5,000' });
                                }
                                if (value > 25000) {
                                  return this.createError({ message: 'Limit maximum is $25,000' });
                                }
                              }
                              return true;
                            }),
                          description: Yup.string()
                            .nullable()
                            .test('description-required-spp', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Description is required' });
                              }
                              return true;
                            }),
                          // #33279: the legacy top-level per-item "Deductible" field was removed from
                          // the UI in favor of the "All peril deductible" on
                          // extendedDetails.deductibleAllOther, where required-ness is now enforced.
                          // Keep the field nullable so older persisted items still validate.
                          deductible: Yup.number().nullable(),
                          extendedDetails: Yup.object()
                            .nullable()
                            .default(undefined)
                            .shape({
                              name: Yup.string()
                                .nullable()
                                .test('extName-required-by-coverage-type', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                    return this.createError({ message: 'Name is required' });
                                  }
                                  if (ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES && !value) {
                                    return this.createError({ message: 'Structure name is required' });
                                  }
                                  return true;
                                }),
                              year: Yup.number()
                                .nullable()
                                .test('year-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiringYear = ['boat', 'atv', GATOR_UTV, 'golfCart', 'unmannedAircraft'];
                                  if (!typesRequiringYear.includes(category)) {
                                    return true;
                                  }
                                  // #33277: Miscellaneous boating equipment hides the boat-specific fields.
                                  if (isMiscBoat(category, this.parent?.classification)) {
                                    return true;
                                  }
                                  if (value == null) {
                                    return this.createError({ message: 'Year is required' });
                                  }
                                  if (value < 1900) {
                                    return this.createError({ message: 'Year must be 1900 or later' });
                                  }
                                  const currentYear = new Date().getFullYear();
                                  if (value > currentYear) {
                                    return this.createError({
                                      message: `Year must be ${currentYear} or earlier`
                                    });
                                  }
                                  return true;
                                }),
                              horsepower: Yup.number()
                                .nullable()
                                .test('horsepower-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  // #33277: Miscellaneous boating equipment hides the boat-specific fields.
                                  if (
                                    category === 'boat' &&
                                    !isMiscBoat(category, this.parent?.classification) &&
                                    value == null
                                  ) {
                                    return this.createError({ message: 'Horsepower is required' });
                                  }
                                  return true;
                                }),
                              serialNumber: Yup.string()
                                .nullable()
                                .test('serialNumber-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['boat', 'unmannedAircraft', 'atv', GATOR_UTV, 'golfCart'];
                                  // #33277: Miscellaneous boating equipment hides the boat-specific fields.
                                  if (
                                    typesRequiring.includes(category) &&
                                    !isMiscBoat(category, this.parent?.classification) &&
                                    !value
                                  ) {
                                    return this.createError({ message: 'Serial Number is required' });
                                  }
                                  return true;
                                }),
                              classification: Yup.string()
                                .nullable()
                                .test('classification-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boat' && !value) {
                                    return this.createError({ message: 'Classification is required' });
                                  }
                                  return true;
                                }),
                              make: Yup.string()
                                .nullable()
                                .test('make-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['golfCart', 'atv', GATOR_UTV, 'unmannedAircraft'];
                                  if (typesRequiring.includes(category) && !value) {
                                    return this.createError({ message: 'Make is required' });
                                  }
                                  return true;
                                }),
                              model: Yup.string()
                                .nullable()
                                .test('model-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['golfCart', 'atv', GATOR_UTV, 'unmannedAircraft'];
                                  if (typesRequiring.includes(category) && !value) {
                                    return this.createError({ message: 'Model is required' });
                                  }
                                  return true;
                                }),
                              cc: Yup.mixed()
                                .nullable()
                                .test('cc-required-atv', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (isAtvLikeCategory(category) && (value == null || value === '')) {
                                    return this.createError({ message: 'CC is required' });
                                  }
                                  return true;
                                }),
                              weight: Yup.number()
                                .nullable()
                                .test('weight-required-drone', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'unmannedAircraft' && value == null) {
                                    return this.createError({ message: 'Weight is required' });
                                  }
                                  return true;
                                }),
                              lengthFt: Yup.number()
                                .nullable()
                                .test('lengthFt-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  // #33277: Miscellaneous boating equipment hides the boat-specific fields.
                                  if (
                                    category === 'boat' &&
                                    !isMiscBoat(category, this.parent?.classification) &&
                                    value == null
                                  ) {
                                    return this.createError({ message: 'Length is required' });
                                  }
                                  return true;
                                }),
                              milesPerHour: Yup.number()
                                .nullable()
                                .test('milesPerHour-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  // #33277: Miscellaneous boating equipment hides the boat-specific fields.
                                  if (
                                    category === 'boat' &&
                                    !isMiscBoat(category, this.parent?.classification) &&
                                    value == null
                                  ) {
                                    return this.createError({ message: 'Miles per hour is required' });
                                  }
                                  return true;
                                }),
                              dimensions: Yup.string()
                                .nullable()
                                .test('dimensions-required-boatDock', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boatDock' && !value) {
                                    return this.createError({ message: 'Dimensions are required' });
                                  }
                                  return true;
                                }),
                              kindOfAnimal: Yup.string().nullable(),
                              sex: Yup.string()
                                .nullable()
                                .test('sex-required-animals', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && !value) {
                                    return this.createError({ message: 'Sex is required' });
                                  }
                                  return true;
                                }),
                              breed: Yup.string().nullable(),
                              age: Yup.number().nullable(),
                              originalCost: Yup.number()
                                .nullable()
                                .test('originalCost-required-animals', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && value == null) {
                                    return this.createError({ message: 'Original cost is required' });
                                  }
                                  return true;
                                }),
                              coinsurance: Yup.mixed()
                                .nullable()
                                .test('coinsurance-required-income', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.INCOME && (value == null || value === '')) {
                                    return this.createError({ message: 'Coinsurance is required' });
                                  }
                                  return true;
                                }),
                              days: Yup.mixed().nullable(),
                              startDate: Yup.string()
                                .nullable()
                                .test('startDate-required-peak', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                    return this.createError({ message: 'Start date is required' });
                                  }
                                  return true;
                                }),
                              endDate: Yup.string()
                                .nullable()
                                .test('endDate-required-and-after-start-peak', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.PEAK) {
                                    return true;
                                  }
                                  if (!value) {
                                    return this.createError({ message: 'End date is required' });
                                  }
                                  const startDate = this.parent?.startDate;
                                  if (startDate && new Date(value) < new Date(startDate)) {
                                    return this.createError({
                                      message: 'End date must be after start date'
                                    });
                                  }
                                  return true;
                                }),
                              description: Yup.string()
                                .nullable()
                                .test('extDescription-required-rps', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES && !value) {
                                    return this.createError({ message: 'Description is required' });
                                  }
                                  return true;
                                }),
                              deductibleAllOther: Yup.mixed()
                                .nullable()
                                .test('deductibleAllOther-required', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  const isEmpty = value == null || value === '';
                                  if (ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES && isEmpty) {
                                    return this.createError({ message: 'All peril deductible is required' });
                                  }
                                  // #33279: the All-Peril deductible replaces the removed legacy per-item
                                  // deductible for inland-marine (SPP) items, using the same gating that
                                  // the legacy field used (always-required categories + non-IL states).
                                  if (
                                    ct === FarmScheduleCoverageType.SPP &&
                                    isEmpty &&
                                    sppDeductibleRequired(this.from?.[1]?.value?.category, this.options?.context)
                                  ) {
                                    return this.createError({ message: 'All peril deductible is required' });
                                  }
                                  return true;
                                }),
                              deductibleWindHail: Yup.mixed()
                                .nullable()
                                .test('deductibleWindHail-required-rps', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (
                                    ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES &&
                                    (value == null || value === '')
                                  ) {
                                    return this.createError({ message: 'Wind & Hail deductible is required' });
                                  }
                                  return true;
                                }),
                              coverageMS: Yup.boolean().nullable(),
                              hasActiveCoverageMS: Yup.boolean().nullable()
                            })
                        })
                      )
                      .nullable()
                  })
                  .nullable()
              )
              .nullable()
              .test(
                'required-schedule-coverages-for-fo6-scheduled',
                'At least one schedule coverage is required',
                (value, { options }) => {
                  const [locationPath, riskPath] = options.path.split('.');
                  const risk = getIn(options.context, `${locationPath}.${riskPath}`);
                  if (!risk || risk.type !== FarmRiskType.Fo6Scheduled) {
                    return true;
                  }
                  // #34585: there is deliberately no `!risk.id` rated-only guard. This rule used to
                  // bail on an unrated risk, so it never fired on the new-offer form — letting
                  // "See price" submit an item-less FO-6 Scheduled risk. Its Coverage F items ARE
                  // its entire premium, so ClarionDoor rates it at $0, returns a blank error text,
                  // and the all-or-nothing farm rate fails with BLOCK 5042. The risk must satisfy
                  // this BEFORE its first rate, exactly as required-inland-marine-item below does.
                  // Adding the first item is not deadlocked: the FO6-scheduled card's add-item
                  // handler drops this error while the list is still empty (#29083/#29771).
                  //
                  // coverageF items satisfy the requirement (they ARE scheduled farm property items) (#29771).
                  // #34585: via the shared predicate, so "populated" means the same thing here as it
                  // does on the new-offer form. A length-only check counts a category picked with no
                  // limit, which rates at exactly $0 and reproduces the BLOCK 5042 this rule exists
                  // to prevent — the same FE/BE divergence hasInlandMarineSppItems was created to close.
                  if (hasRateableScheduledFarmPropertyItems(risk)) {
                    return true;
                  }
                  // It's OK to have no scheduled items when the location has other rated risks
                  // besides FO6 Scheduled/Unscheduled (e.g., FO1-FO4 or FO6 Structures) (#29818)
                  const location = getIn(options.context, locationPath);
                  const hasOtherRatedRisks =
                    Array.isArray(location?.risks) &&
                    location.risks.some(
                      (r) => r.id && r.type !== FarmRiskType.Fo6Scheduled && r.type !== FarmRiskType.Fo6Unscheduled
                    );
                  if (hasOtherRatedRisks) {
                    return true;
                  }
                  // Otherwise check scheduleCoverages
                  if (!Array.isArray(value) || value.length === 0) {
                    return false;
                  }
                  return value.some((coverage) => Array.isArray(coverage?.items) && coverage.items.length > 0);
                }
              )
              .test(
                'required-inland-marine-item',
                'At least one inland marine item is required',
                (_value, { options }) => {
                  // `from` walks the schema outwards from this field, so [0] is `coverages` and [1] the
                  // risk. The FO6-scheduled rule above parses `options.path` instead; that resolves the
                  // risk only while the nesting is exactly farmLocations[i].risks[j].coverages, and
                  // returns valid when it can't — silently disabling the rule that keeps an un-rateable
                  // offer out of ClarionDoor.
                  const risk = options.from?.[1]?.value;
                  const location = options.from?.[2]?.value;
                  if (risk?.type !== FarmRiskType.InlandMarine) {
                    return true;
                  }
                  // The farmLocations array schema validates soft-deleted entries too, and their cards
                  // no longer render — so without this the error would be unfixable.
                  if (risk.isDeleted || location?.isDeleted) {
                    return true;
                  }
                  // #33164 (docs/farm-standalone-inland-marine.md). Two deliberate differences from the
                  // FO6-scheduled rule above:
                  //
                  // 1. No `!risk.id` rated-only guard. A standalone inland marine risk must satisfy this
                  //    BEFORE its first rate: its SPP items are its entire premium, and a farm offer's
                  //    total premium can never be $0, so a risk with none fails outright at ClarionDoor
                  //    with "did not receive premium". A populated sibling inland marine risk does not
                  //    save it — CFM rates one ClarionDoor request per risk, and any errored rate fails
                  //    the whole all-or-nothing farm rate.
                  // 2. No "the location has other rated risks" escape. Inland marine is only ever
                  //    written on its own, so there is no sibling risk whose premium could stand in for
                  //    it.
                  return hasInlandMarineSppItems(risk);
                }
              ),
            coverageWaterBackup: Yup.number().nullable(),
            coverageISBC: Yup.number().nullable(),
            coverageRSExtended: Yup.boolean().nullable(),
            poolLimit: Yup.number()
              .nullable()
              .test('required-pool-limit', 'Coverage limit is required', function (value) {
                // Pool limit is required only for above-ground pools on FO1-FO4 risks (#29233).
                // In-ground pools no longer expose a dwelling-level coverage limit — agents
                // schedule the pool under Related Private Structures instead (#30984).
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }

                const riskType = formRiskData.type;

                if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
                  return true;
                }

                const typeOfPool = formRiskData.building?.swimmingPool?.typeOfPool;

                if (typeOfPool === 'A' && (value === null || value === undefined)) {
                  return this.createError({ message: 'Coverage limit is required' });
                }

                // #37093: an above-ground pool limit GO wrote below the base, imported as approved
                // (poolLimitOverride), stands while the limit is unchanged.
                if (
                  typeOfPool === 'A' &&
                  value < ABOVE_GROUND_POOL_INCLUDED_LIMIT &&
                  !isPoolLimitApproved(this.parent)
                ) {
                  return this.createError({
                    message: 'Above-ground pool total coverage cannot be less than the included $2,500 base'
                  });
                }

                return true;
              }),
            resUnderConstructionLimit: Yup.number().nullable(),
            // The effective date is required once a "Dwelling under construction (theft)" limit is
            // entered, so an applied coverage can't be sent to underwriting/issued without it (#34985).
            resUnderConstructionEffectiveDate: Yup.string()
              .nullable()
              .when('resUnderConstructionLimit', {
                is: (value) => value != null && value > 0,
                // `.nullable()` so a missing date yields the required message rather than a
                // "must be a string" type error on the `null` value.
                then: Yup.string().nullable().required('Effective date is required'),
                otherwise: Yup.string().nullable()
              }),
            inlandFloodInceptionDate: Yup.string().nullable(),
            inlandFloodOccurrenceLimit: Yup.number().nullable(),
            inlandFloodAggregateLimit: Yup.number().nullable(),
            inlandFloodDeductible: Yup.number().nullable(),
            inlandFloodDeductibleOverride: Yup.number().nullable(),
            inlandFloodRiskScore: Yup.number().nullable(),
            expandedDebrisRemoval: Yup.boolean().nullable(),
            serviceLine: Yup.boolean().nullable(),
            personalPropertyReplCost: Yup.boolean().nullable(),
            businessPropertyLimit: Yup.number().nullable(),
            businessPropertyLimitDetails: Yup.string().nullable(),
            moneyLimit: Yup.number().nullable(),
            jewelryLimit: Yup.number().nullable(),
            securitiesLimit: Yup.number().nullable(),
            motorizedVehiclesLimit: Yup.number().nullable(),
            silverwareLimit: Yup.number().nullable(),
            gunsLimit: Yup.number().nullable(),
            tenantImprovementLimit: Yup.number().nullable(),
            sprinklerLeakageBuildingLimit: Yup.number().nullable(),
            sprinklerLeakageLimitDetails: Yup.string()
              .nullable()
              .when('sprinklerLeakageBuildingLimit', {
                is: (value) => value != null && value > 0,
                // `.nullable()` so a missing description yields the required message rather than a
                // "must be a string" type error on a `null` value (same Yup 0.32 gotcha as
                // resUnderConstructionEffectiveDate above, #34985).
                then: Yup.string()
                  .nullable()
                  .required('Description of premise is required when sprinkler leakage limit is provided'),
                otherwise: Yup.string().nullable()
              }),
            canEditCoverageAmount: Yup.boolean().nullable(),
            // #34201: UW-only one-shot — when set, the next rate refreshes migrated Coverage A/E from
            // a fresh 360Value pull, then it auto-clears.
            uwRefresh360Value: Yup.boolean().nullable(),
            // #34201: persisted UW approval that Coverage A may sit below the normal floor (down to
            // $1,000, no cap). Survives re-rates/agent binds so a UW-lowered value isn't re-blocked.
            coverageAFloorApprovedByUW: Yup.boolean().nullable(),
            adjustDwellingLimitDescription: Yup.string()
              // #33448: this field is shared by FO-1 (dwelling limit) and FO-6 (scheduled property
              // limit), so the required message must be coverage-neutral rather than naming "dwelling".
              // Inlined (instead of the shared requiredWhenCanEditCoverageAmount helper) to add the
              // isNewRisk guard: pre-rate the FO-6 UI hides this field and locks the toggle, so a stale
              // canEditCoverageAmount=true on an un-rated risk must not raise a required error the agent
              // can neither satisfy nor clear. Mirrors the cosmeticDamageExclusion guard just below.
              .test(
                'required-when-can-edit-coverage-amount',
                'Description of reason for adjusting the coverage limit is required',
                function (value) {
                  const formRiskData = this.from?.[1]?.value;
                  if (!getIn(this.parent, 'canEditCoverageAmount') || isNewRisk(formRiskData)) {
                    return true;
                  }
                  return value !== undefined && value !== null;
                }
              )
              .nullable(),
            cosmeticDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Cosmetic damage exclusion is required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                // A UW override (#35870) includes cosmetic damage, i.e. turns all four flags off. #37040
                if (riskType === FarmRiskType.Fo6Structures && !isCosmeticDamageUwOverridden(this.parent)) {
                  return value === true;
                }
                return true;
              })
              .test(
                'at-least-one-exclusion-type',
                'You must have at least one cosmetic damage surfacing type selected in order to exclude it.',
                function (value) {
                  if (!value) {
                    return true;
                  }

                  const { exteriorDoorsWindowsDamageExclusion, exteriorWallsDamageExclusion, roofsDamageExclusion } =
                    this.parent;

                  return exteriorDoorsWindowsDamageExclusion || exteriorWallsDamageExclusion || roofsDamageExclusion;
                }
              ),
            exteriorDoorsWindowsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                // A UW override (#35870) includes cosmetic damage, i.e. turns all four flags off. #37040
                if (riskType === FarmRiskType.Fo6Structures && !isCosmeticDamageUwOverridden(this.parent)) {
                  return value === true;
                }
                return true;
              }),
            exteriorWallsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                // A UW override (#35870) includes cosmetic damage, i.e. turns all four flags off. #37040
                if (riskType === FarmRiskType.Fo6Structures && !isCosmeticDamageUwOverridden(this.parent)) {
                  return value === true;
                }
                return true;
              }),
            roofsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                // A UW override (#35870) includes cosmetic damage, i.e. turns all four flags off. #37040
                if (riskType === FarmRiskType.Fo6Structures && !isCosmeticDamageUwOverridden(this.parent)) {
                  return value === true;
                }
                return true;
              })
          }),
          connectedHome: Yup.object().shape({
            monitored: Yup.string().nullable(),
            moistureDevices: Yup.number().nullable(),
            theftPreventionDevices: Yup.number().nullable(),
            theftPreventionDeviceType: Yup.mixed().nullable(),
            motionDetectingDevices: Yup.number().nullable(),
            smokeDetectingDevices: Yup.number().nullable(),
            fireProtectiveDeviceType: Yup.mixed().nullable(),
            internetMonitoring: Yup.boolean().nullable(),
            autoWaterShutoffDevices: Yup.boolean().nullable(),
            providerName: Yup.string().nullable(),
            agreeToTerms: Yup.boolean().nullable()
          })
        })
      )
    })
  ),
  farmInterestedParties: Yup.array()
    .when('global.farmPaymentMethod', {
      is: (farmPaymentMethod) => farmPaymentMethod === paymentMethod.BillToOther,
      then: Yup.array()
        .test(
          'billToOther-exists',
          'A Bill To Other party is required when Bill To Other payment method is selected',
          (farmInterestedParties) => {
            if (!farmInterestedParties || farmInterestedParties.length === 0) {
              return false;
            }
            return farmInterestedParties.some((party) => party.relationship === 'billToOther');
          }
        )
        .of(
          Yup.object().when('relationship', {
            is: 'billToOther',
            then: Yup.object().shape({
              name: Yup.string().required('Bill To Other name is required').nullable(),
              address: Yup.object()
                .shape({
                  address: Yup.string().required('Bill To Other address is required').nullable(),
                  address2: Yup.string().nullable(),
                  city: Yup.string().required('Bill To Other city is required').nullable(),
                  state: Yup.string()
                    .validUSState('address.state')
                    .required('Bill To Other state is required')
                    .nullable(),
                  zip: Yup.string().required('Bill To Other zip is required').nullable()
                })
                .required('Bill To Other address is required')
                .nullable()
            }),
            otherwise: getFarmInterestedPartyItemSchema(false)
          })
        )
        .nullable(),
      otherwise: Yup.array().of(getFarmInterestedPartyItemSchema(false)).nullable()
    })
    .nullable(),
  drivers: Yup.array()
    .of(
      Yup.object().shape({
        dateOfBirth: Yup.string()
          .nullable()
          .required('Date of birth is required')
          .test('minimum-driver-age', '', function (value) {
            if (value) {
              const driver = this.options?.context?.drivers?.[this.options?.index];
              const firstName = driver?.firstName ?? '';
              const lastName = driver?.lastName ?? '';
              const today = new Date();
              const birthDate = new Date(value);
              const age = differenceInYears(today, birthDate);

              if (age < 14) {
                return this.createError({
                  message: `${firstName} ${lastName} is too young to be on the policy`,
                  path: this.path
                });
              }
            }
            return true;
          }),
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required')
      })
    )
    .nullable(),
  farmLiabilityCoverage: Yup.object()
    .nullable()
    .when('includeFarmLiability', {
      is: true,
      then: (schema) =>
        schema.shape({
          // Issue #31838: when liability is toggled on the agent must first pick a
          // coverage type. Without this rule the only liability error was the
          // farmOperations one below, so an agent who hadn't picked a type saw
          // "Primary farm operations is required" (a field that's even hidden for
          // Home/Rental) instead of the real problem.
          type: Yup.string().nullable().required('Please select type of liability coverage'),
          // Issue #31351: Clarion Door rejects the rate request when
          // `primary_operations` is empty, so the field must be picked before
          // submit whenever liability is included. Gated on `type` so that when no
          // type is selected yet the agent sees the type error first (#31838) — and
          // because farmOperations is only meaningful/visible once a type is chosen.
          farmOperations: Yup.string()
            .nullable()
            .when('type', {
              is: (type) => Boolean(type),
              then: (schema) => schema.required('Primary farm operations is required'),
              otherwise: (schema) => schema.nullable()
            }),
          domesticEmployees: Yup.array()
            .of(
              Yup.object().shape({
                firstName: Yup.string().required('First name is required'),
                lastName: Yup.string().required('Last name is required')
              })
            )
            .nullable(),
          occupancies: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Occupancy type is required'),
                description: Yup.string().required('Occupancy description is required'),
                location: Yup.string().required('Location description is required')
              })
            )
            .nullable(),
          customFarmWorkItems: Yup.array()
            .of(
              Yup.object().shape({
                description: Yup.string().required('Description is required'),
                amount: Yup.number().required('Estimated receipts is required').positive('Must be a positive number')
              })
            )
            .nullable(),
          businessActivities: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Business activity type is required'),
                description: Yup.string().required('Description is required'),
                includesSpecificActivities: Yup.boolean().nullable(),
                namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
              })
            )
            .nullable(),
          businessPursuits: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.number().required('Type is required'),
                amount: Yup.number().required('Estimated receipts is required'),
                namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
              })
            )
            .nullable(),
          customFeeding: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Operations is required'),
                description: Yup.string().required('Description is required'),
                associatedLocations: Yup.array().of(Yup.string().required('Associated location is required'))
              })
            )
            .nullable(),
          additionalAssetsExtensions: Yup.array()
            .of(
              Yup.object().shape({
                coverage: Yup.string().nullable().required('Coverage is required'),
                name: Yup.string()
                  .nullable()
                  .test('asset-name-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', GATOR_UTV, 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type) && !value) {
                      return this.createError({ message: 'Name is required' });
                    }
                    return true;
                  }),
                year: Yup.number()
                  .nullable()
                  .test('asset-year-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (!['atv', GATOR_UTV, 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type)) {
                      return true;
                    }
                    if (value == null) {
                      return this.createError({ message: 'Year is required' });
                    }
                    if (value < 1900) {
                      return this.createError({ message: 'Year must be 1900 or later' });
                    }
                    const currentYear = new Date().getFullYear();
                    if (value > currentYear) {
                      return this.createError({ message: `Year must be ${currentYear} or earlier` });
                    }
                    return true;
                  }),
                make: Yup.string()
                  .nullable()
                  .test('asset-make-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', GATOR_UTV, 'golfCart', 'unmannedAircraft'].includes(item.type) && !value) {
                      return this.createError({ message: 'Make is required' });
                    }
                    return true;
                  }),
                model: Yup.string()
                  .nullable()
                  .test('asset-model-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', GATOR_UTV, 'golfCart', 'unmannedAircraft'].includes(item.type) && !value) {
                      return this.createError({ message: 'Model is required' });
                    }
                    return true;
                  }),
                serialNumber: Yup.string()
                  .nullable()
                  .test('asset-serialNumber-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', GATOR_UTV, 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type) && !value) {
                      return this.createError({ message: 'Serial number is required' });
                    }
                    return true;
                  }),
                cc: Yup.mixed()
                  .nullable()
                  .test('asset-cc-required-atv', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (isAtvLikeCategory(item.type) && (value == null || value === '')) {
                      return this.createError({ message: 'CC is required' });
                    }
                    return true;
                  }),
                weight: Yup.number()
                  .nullable()
                  .test('asset-weight-required-drone', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'unmannedAircraft' && value == null) {
                      return this.createError({ message: 'Weight is required' });
                    }
                    return true;
                  }),
                description: Yup.string()
                  .nullable()
                  .test('asset-description-required-drone', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'unmannedAircraft' && !value) {
                      return this.createError({ message: 'Description is required' });
                    }
                    return true;
                  }),
                classification: Yup.string()
                  .nullable()
                  .test('asset-classification-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && !value) {
                      return this.createError({ message: 'Classification is required' });
                    }
                    return true;
                  }),
                horsepower: Yup.number()
                  .nullable()
                  .test('asset-horsepower-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Horsepower is required' });
                    }
                    return true;
                  }),
                length: Yup.number()
                  .nullable()
                  .test('asset-length-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Length is required' });
                    }
                    return true;
                  }),
                mph: Yup.number()
                  .nullable()
                  .test('asset-mph-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Miles per hour is required' });
                    }
                    return true;
                  })
              })
            )
            .nullable(),
          additionalPropertiesExtensions: Yup.array()
            .of(
              Yup.object().shape({
                coverage: Yup.string().nullable().required('Coverage is required'),
                additionalLegalDescription: Yup.string().nullable(),
                locationDirections: Yup.string().nullable(),
                structureDetail: Yup.string().nullable(),
                numFamilies: Yup.number()
                  .nullable()
                  .test('numFamilies-required-rented-non-structure', function (value) {
                    const item = this.parent;
                    const RENTED_COVERAGES_SET = new Set([
                      'locationRentedToOthers',
                      'structureRentedToOthers',
                      'residenceRentedToOthers'
                    ]);
                    if (!RENTED_COVERAGES_SET.has(item.coverage)) {
                      return true;
                    }
                    if (item.type === 'structure') {
                      return true;
                    }
                    if (value == null) {
                      return this.createError({ message: 'Number of families is required' });
                    }
                    return true;
                  }),
                description: Yup.string()
                  .nullable()
                  .test('property-description-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId) {
                      return true;
                    }
                    if (item.type === 'location' && !value) {
                      return this.createError({ message: 'Location name is required' });
                    }
                    if ((item.type === 'residence' || item.type === 'structure') && !value) {
                      return this.createError({ message: 'Item name is required' });
                    }
                    return true;
                  }),
                numberOfAcres: Yup.number()
                  .nullable()
                  .integer('Must be a whole number')
                  .test('property-numberOfAcres-required-location', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId) {
                      return true;
                    }
                    if (item.type === 'location' && value == null) {
                      return this.createError({ message: 'Acreage is required' });
                    }
                    return true;
                  }),
                legalDescription: Yup.object()
                  .nullable()
                  .default(undefined)
                  .shape({
                    format: Yup.string()
                      .nullable()
                      .test('legalDescription-format-required-location', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && !value) {
                          return this.createError({ message: 'Legal Description Format is required' });
                        }
                        return true;
                      }),
                    section: Yup.string()
                      .nullable()
                      .test('legalDescription-section-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Section is required' });
                        }
                        return true;
                      }),
                    township: Yup.string()
                      .nullable()
                      .test('legalDescription-township-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Township is required' });
                        }
                        return true;
                      }),
                    range: Yup.string()
                      .nullable()
                      .test('legalDescription-range-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Range is required' });
                        }
                        return true;
                      }),
                    lot: Yup.string()
                      .nullable()
                      .test('legalDescription-lot-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'lot-block' && !value) {
                          return this.createError({ message: 'Lot is required' });
                        }
                        return true;
                      }),
                    block: Yup.string()
                      .nullable()
                      .test('legalDescription-block-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'lot-block' && !value) {
                          return this.createError({ message: 'Block is required' });
                        }
                        return true;
                      }),
                    townshipCoordinates: Yup.string().nullable(),
                    rangeCoordinates: Yup.string().nullable()
                  }),
                addressDetails: Yup.object()
                  .nullable()
                  .default(undefined)
                  .shape({
                    address: Yup.string()
                      .nullable()
                      .test('addressDetails-address-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'residence' && !value) {
                          return this.createError({ message: 'Address is required' });
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'street' && !value) {
                          return this.createError({ message: 'Address is required' });
                        }
                        return true;
                      }),
                    address2: Yup.string().nullable(),
                    city: Yup.string()
                      .nullable()
                      .test('addressDetails-city-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'City is required' });
                        }
                        return true;
                      }),
                    state: Yup.string()
                      .nullable()
                      .test('addressDetails-state-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'State is required' });
                        }
                        return true;
                      }),
                    zip: Yup.string()
                      .nullable()
                      .test('addressDetails-zip-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'ZIP is required' });
                        }
                        return true;
                      }),
                    county: Yup.string()
                      .nullable()
                      .test('addressDetails-county-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type !== 'location') {
                          return true;
                        }
                        const format = item.legalDescription?.format;
                        if (format && format !== 'street' && !value) {
                          return this.createError({ message: 'County is required' });
                        }
                        return true;
                      })
                  })
              })
            )
            .nullable(),
          careProvidedToOthers: Yup.number().nullable(),
          careProvidedToOthersDescription: Yup.string()
            .nullable()
            .when('careProvidedToOthers', {
              is: (value) => value > 0,
              then: (schema) => schema.required('Description is required'),
              otherwise: (schema) => schema.nullable()
            }),
          // Employer's-liability employee counts are NOT required. The Employer's Liability section is
          // mask-hidden on Rental (Y) and Home (Z), and migrated policies came over with
          // `employees: null` (the converter never populated them) — an unconditional `.required()`
          // here fired three "required field" errors on every save and blocked ALL edits to those
          // policies (#33009). Keep the range/integer constraints, but only when a value is actually
          // entered (new-business offers still seed 0s via buildEmptyFarmLiabilityCoverage).
          employees: Yup.object()
            .nullable()
            .shape({
              fullTime: Yup.number()
                .nullable()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number'),
              partTime: Yup.number()
                .nullable()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number'),
              // #35073: partTime40DaysOrLess holds MAN-DAYS (not a headcount), so it can exceed
              // 99 (several workers × up to 40 days each). Cap at 9999.
              partTime40DaysOrLess: Yup.number()
                .nullable()
                .min(0, 'Must be 0 or greater')
                .max(9999, 'Must be 9999 or less')
                .integer('Must be a whole number'),
              // #35073: count of part-time employees working 40 days or less — a headcount, so
              // capped at 99 like fullTime/partTime. This is the value rating uses.
              // #35153: on the CFM path (featureFlags.hideFarmManMonths — the only brand where this
              // count drives rating; Forreston rates on man-months and ignores it) this count must
              // be > 0 whenever part-time man-days are entered. It is a .test (not a plain .when)
              // because it must ALSO skip when the whole Employer's Liability section is hidden by
              // the policy-type mask — CFM Home (Z) / Rental (Y) drop it — otherwise a Z/Y offer or
              // policy carrying stale/imported man-days > 0 would hit a required field with no
              // on-screen way to satisfy it: the exact #33009 lockout. So the rule only fires where
              // the field is actually rendered (man-days > 0 AND the section isn't mask-hidden),
              // mirroring liability-coverages.jsx's isLiabilityCoverageHiddenByMask gate.
              partTime40DaysOrLessEmployees: Yup.number()
                .nullable()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number')
                .test(
                  'partTimeEmployeeCountRequiredWhenManDaysEntered',
                  'Number of part-time employees (40 days or less) is required (must be greater than 0)',
                  function (value) {
                    if (!appConfig?.featureFlags?.hideFarmManMonths) {
                      return true;
                    }
                    const partTimeManDays = this.parent?.partTime40DaysOrLess || 0;
                    if (partTimeManDays <= 0) {
                      return true;
                    }
                    // Resolve the farm policy type + selected liability type + state the same way the
                    // form does, so this rule fires only where the section renders. Read from the
                    // root value (this.from's outermost frame), falling back to the validation context
                    // (offer/policy both spread the form values into context).
                    const ctx = this.options?.context || {};
                    const root = this.from?.[this.from.length - 1]?.value || ctx;
                    const farmPolicyType =
                      root.policyType || root.selectedOption || ctx.policyType || ctx.selectedOption;
                    const liabilityType = root.farmLiabilityCoverage?.type ?? ctx.farmLiabilityCoverage?.type;
                    const state = ctx.locationAddress?.state ?? ctx.state ?? root.farmLocations?.[0]?.address?.state;
                    if (isLiabilityCoverageHiddenByMask(farmPolicyType, state, liabilityType, 'employersLiability')) {
                      return true;
                    }
                    return typeof value === 'number' && value > 0;
                  }
                )
            }),
          exclusions: Yup.object()
            .nullable()
            .shape({
              excludeActivities: Yup.boolean().nullable(),
              excludedActivitiesDescription: Yup.string()
                .nullable()
                .when('excludeActivities', {
                  is: true,
                  then: (schema) => schema.required('Description of excluded activities is required'),
                  otherwise: (schema) => schema.nullable()
                }),
              excludeLocations: Yup.boolean().nullable(),
              excludedLocationsDescription: Yup.string()
                .nullable()
                .when('excludeLocations', {
                  is: true,
                  then: (schema) => schema.required('Description of excluded locations is required'),
                  otherwise: (schema) => schema.nullable()
                })
            })
        })
    })
});

/** Path of the first live inland marine risk's `type` select, or null if there isn't one. */
const getFirstInlandMarineRiskTypePath = (farmLocations) => {
  for (const [locationIndex, location] of (farmLocations || []).entries()) {
    if (!location || location.isDeleted) {
      continue;
    }
    for (const [riskIndex, risk] of (location.risks || []).entries()) {
      if (risk && !risk.isDeleted && isInlandMarineRiskType(risk.type)) {
        return `farmLocations[${locationIndex}].risks[${riskIndex}].type`;
      }
    }
  }
  return null;
};

/**
 * Inland marine can only be quoted on its own (#33164 — docs/farm-standalone-inland-marine.md).
 *
 * Root-level because the rule is about which risks COEXIST, so it needs the whole form, not one risk.
 * The UI already makes a mixed offer unreachable by filtering the add-risk options
 * (applyInlandMarineExclusivity), which is the primary guard; this is the backstop for a payload that
 * got there another way — a legacy offer, or an offer created through the API. Worth having because
 * the BE *throws* on a mixed payload at every boundary that persists farm risks, so without this the
 * agent would see a hard error instead of a fixable form message.
 *
 * Reads the product from `policyType` (policy route) or `selectedOption` (offer route) — the two
 * routes differ. Gated on the BE's own isInlandMarineAvailableForPolicyType (F and the FA bundle)
 * rather than a restated 'F': hasMixedInlandMarineRiskTypes is product-agnostic, so this gate is the
 * only thing scoping the rule. `allowedRiskTypes` keeps a brand that cannot quote inland marine from
 * tripping the rule at all.
 */
const inlandMarineExclusivitySchema = Yup.object()
  .shape({})
  .test('inland-marine-exclusivity', function (value) {
    const farmPolicyType = value?.policyType || value?.selectedOption;
    if (farmPolicyType && !isInlandMarineAvailableForPolicyType(farmPolicyType)) {
      return true;
    }

    const state = value?.farmLocations?.[0]?.address?.state;
    const allowedRiskTypes = [...baseFarmRiskTypes, ...getAdditionalFarmRiskTypes(state)];
    if (!hasMixedInlandMarineRiskTypes(value?.farmLocations, allowedRiskTypes)) {
      return true;
    }

    return this.createError({
      // Formik's yupToFormErrors drops a parent-level `farmLocations` error whenever a nested
      // farmLocations[i] error coexists, so this has to land on a leaf that renders.
      path: getFirstInlandMarineRiskTypePath(value?.farmLocations) ?? 'farmLocations',
      message:
        'Inland marine can only be quoted on its own — an offer or policy cannot hold an inland marine risk alongside a dwelling, renters or other farm property risk. Multiple inland marine risks are allowed.'
    });
  });

// Issue #34087: a farm offer must insure SOMETHING. A location with no risks is a legitimate
// liability-only shape only when farm liability is actually included; with liability off too the
// offer covers nothing at all. The backend enforces this at the pre-bind gate
// (shared/applications/pre-bind-helpers/.../pre-bind-validations/index.ts), but that only fires at
// the very end of checkout — the agent could uncheck liability, send to UW, get approved and reach
// the purchase before hitting it. This shared schema runs on every "update offer price", "send to
// underwriting" and "approve for checkout" action (all funnel through Formik validateForm), so
// checking it here surfaces the same warning at each of those earlier steps for both agents and UWs.
//
// Mirrors the backend exactly: skip when there are no farm locations (matches its early return, and
// keeps non-farm/empty submissions from tripping this), and treat `includeFarmLiability !== true`
// (including undefined) as liability-off. The error is raised on its own synthetic blocking field
// `farmInsuresNothing` (NOT the shared rejections field) so the validation-errors modal renders it in
// the red "Blocking issues" section; a separate path is required because rejectionsSchema writes the
// rejections field from a shape-level test that Yup runs before this whole-object test, and Formik
// keeps only the first error per path — so reusing it would let an active rejection silently swallow
// this warning at the very UW-approval step it targets (see FARM_INSURES_NOTHING_FIELD in
// core/components/validation-errors-modal). Applied in getSharedFarmSchema rather than chained onto
// baseFarmValidationSchema so the object literal's formatting stays intact.
const withInsuresSomethingCheck = (schema) =>
  schema.test('farm-insures-something', function (value) {
    const locations = value?.farmLocations;
    if (!locations || locations.length === 0) {
      return true;
    }
    const insuresNothing = locations.every((location) => !location?.risks?.length);
    if (insuresNothing && value?.includeFarmLiability !== true) {
      return this.createError({
        // Keep in sync with FARM_INSURES_NOTHING_FIELD in the validation-errors-modal constants.
        path: 'farmInsuresNothing',
        message:
          'This farm offer insures nothing — no risks on any location and farm liability is not included. ' +
          'Add a risk, or include farm liability to bind it as a liability-only policy.'
      });
    }
    return true;
  });

export const getSharedFarmSchema = async () => {
  let schema = baseFarmValidationSchema.concat(inlandMarineExclusivitySchema);

  try {
    const brandValidationModule = await import(`./brand-schemas/${brandConstants.SHORT_NAME?.toLowerCase()}.js`);

    if (brandValidationModule.default) {
      schema = schema.concat(brandValidationModule.default);
    }
  } catch {
    // Brand validation schema not found - silently continue with base schema
  }

  return withInsuresSomethingCheck(schema);
};
